// Beat Migration Service
// Handles all API calls and data processing for beat migration functionality

import { ChannelkartNetworkGet, ChannelkartNetworkPost } from '../../services/networkService';
import { getLob } from '../../services/authenticationService';

export interface Salesrep {
  id: string;
  name: string;
  code?: string;
  designation?: string;
  loginId: string;
}

export interface Beat {
  id: string; // Unique composite ID for UI operations (beatId + beatName)
  originalBeatId: string; // Original beat ID from API for backend operations
  name: string;
  count: number;
}

export interface ApiResponse {
  features: any[];
}

class BeatMigrationService {

  /**
   * Fetch users with salesrep designation from the API
   */
  async fetchAllUsers(userDesignation: string): Promise<any[]> {
    try {
      const api = `/${userDesignation}`;
      const response = await ChannelkartNetworkGet(api);
      return response.data?.features || [];
    } catch (error: any) {
      console.error('Error fetching users:', error);
      throw new Error(`API Error: ${error.message || 'Failed to fetch users'}`);
    }
  }

  /**
   * Extract designation value from user object (handles both string and array formats)
   */
  private extractDesignation(user: any): string | undefined {
    if (!user.designation) {
      return undefined;
    }

    if (Array.isArray(user.designation)) {
      return user.designation[0];
    }

    return user.designation;
  }

  /**
   * Transform user data to Salesrep interface
   */
  private transformToSalesrep(user: any): Salesrep {
    const id = user.loginId ?? user.id ?? 'unknown';
    const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    const name = user.name ?? (fullName || 'Unknown User');

    return {
      id,
      name,
      code: user.loginId ?? user.id,
      designation: this.extractDesignation(user),
      loginId: user.loginId ?? id
    };
  }

  /**
   * Fetch sales representatives with pagination
   * Note: API already filters by designation:salesrep
   */
  async fetchSalesreps(userDesignation: string): Promise<Salesrep[]> {
    try {
      const allUsers = await this.fetchAllUsers(userDesignation);

      if (allUsers.length === 0) {
        console.warn('No salesreps found');
        return [];
      }

      return allUsers.map(user => this.transformToSalesrep(user));
    } catch (error) {
      console.error('Error fetching salesreps:', error);
      throw error;
    }
  }

  /**
   * Fetch ALL sales representatives (auto-paginate until all data is loaded)
   */
  async fetchAllSalesreps(userDesignation: string): Promise<Salesrep[]> {
    try {
      let allSalesreps: Salesrep[] = [];
      let offset = 0;
      const limit = 500;
      let hasMore = true;

      while (hasMore) {
        const batch = await this.fetchSalesreps(userDesignation);
        allSalesreps = [...allSalesreps, ...batch];

        // If we got less than limit, we've fetched all data
        hasMore = batch.length === limit;
        offset += batch.length;

        console.log(`Fetched ${batch.length} salesreps (total: ${allSalesreps.length})`);
      }

      console.log(`✅ Finished loading all salesreps: ${allSalesreps.length} total`);
      return allSalesreps;
    } catch (error) {
      console.error('Error fetching all salesreps:', error);
      throw error;
    }
  }

  /**
   * Get unique designations from all users (for debugging)
   */
  async getUniqueDesignations(userDesignation: string): Promise<string[]> {
    try {
      const allUsers = await this.fetchAllUsers(userDesignation);
      const allDesignationValues = allUsers
        .map(user => this.extractDesignation(user))
        .filter((value): value is string => Boolean(value));

      return allDesignationValues.filter((value, index, self) => self.indexOf(value) === index);
    } catch (error) {
      console.error('Error fetching designations:', error);
      return [];
    }
  }

  /**
   * Execute beat migration
   * Calls: POST /streamlet with fileId and selection payload
   */
  async executeBeatMigration(
    fromSalesrepId: string,
    toSalesrepId: string,
    beatIds: string[]
  ): Promise<void> {
    try {
      // Generate fileId in DD-MM-YYYY-beat-migration format
      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0');
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const year = now.getFullYear();
      const fileId = `${day}-${month}-${year}-beat-migration`;

      // Generate UUID for requestId
      const requestId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });

      // Get user loginId from localStorage
      const getUserLoginId = () => {
        const authContext = localStorage.getItem('authContext');
        if (authContext) {
          try {
            const authData = JSON.parse(authContext);
            return authData.loginId ?? authData.user?.loginId ?? 'integration_user';
          } catch (e) {
            console.error('Error parsing auth context:', e);
          }
        }
        return 'integration_user'; // fallback
      };

      const requestBody = {
        requestId,
        groupId: `${year}-${month}-${day}`,
        lob: getLob(),
        loginId: getUserLoginId(),
        batchNumber: 0,
        fileId,
        transformerInfo: [
          {
            skipPreprocessing: true,
            skipPersist: true,
            entityName: "OutletDetails",
            transformerId: "SalesrepBeatTransferTransformer",
            operationType: "insert"
          }
        ],
        features: [
          {
            currentSalesRep: fromSalesrepId,
            newSalesRep: toSalesrepId,
            beats: beatIds
          }
        ]
      };

      const api = '/streamlet';
      const response = await ChannelkartNetworkPost(api, requestBody);
      const responseData = response.data;

      // Check if any feature failed
      const failedFeatures = responseData?.features?.filter((feature: any) => feature.status.toLowerCase() === 'failure');

      if (failedFeatures?.length > 0) {
        const errors = failedFeatures.map((f: any) => f.error).join('; ');
        throw new Error(`Migration Failure: ${errors}`);
      }

      // The API returns immediately as per spec
      console.log('Migration request submitted successfully with fileId:', fileId);
    } catch (error: any) {
      console.error('Error executing beat migration:', error);
      throw new Error(`Migration API Error: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Fetch beats for a specific salesrep with pagination
   * Calls: GET /outletDetails/uniqueBeat?salesrep={salesrepId}&size={size}
   */
  async fetchBeatsForSalesrep(salesrepId: string, size: number = 1000): Promise<Beat[]> {
    try {
      const api = `/outletDetails/uniqueBeat?salesrep=${salesrepId}&size=${size}`;
      const response = await ChannelkartNetworkGet(api);
      const data = response.data;
      
      console.log('🔍 Raw API response:', data);

      // Handle different response structures
      let beatsArray = data;

      // Check if data is wrapped in a property
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        // Try common property names
        if (data.beats && Array.isArray(data.beats)) {
          beatsArray = data.beats;
        } else if (data.data && Array.isArray(data.data)) {
          beatsArray = data.data;
        } else if (data.results && Array.isArray(data.results)) {
          beatsArray = data.results;
        } else if (data.features && Array.isArray(data.features)) {
          beatsArray = data.features;
        } else {
          console.error('❌ API response is not an array and no known array property found:', data);
          throw new Error('Invalid API response format. Expected array of beats.');
        }
      }

      if (!Array.isArray(beatsArray)) {
        console.error('❌ No valid beats array found in response:', data);
        throw new Error('Invalid API response format. Expected array of beats.');
      }

      console.log('✅ Extracted beats array:', beatsArray);

      // If no beats found, return empty array
      if (beatsArray.length === 0) {
        console.log('📝 No beats found in API response');
        return [];
      }

      // Transform API response to match our interface
      const transformedBeats = beatsArray
        .map((item: any, index: number) => {
          const originalBeatId = item.beat ?? item.id;
          const beatName = item.beatName ?? item.beat_name ?? item.name;
          const count = item.outletCount ?? item.outlet_count ?? item.count ?? 0;
          
          // Skip entries with null/undefined originalBeatId or beatName
          if (!originalBeatId || !beatName) {
            return null;
          }
          const uniqueId = `${originalBeatId}__${beatName}`;
          
          return {
            id: uniqueId, 
            originalBeatId: originalBeatId, 
            name: beatName,
            count: count
          };
        })
        .filter((beat): beat is Beat => beat !== null); // Remove null entries

      console.log('🎯 Transformed beats with counts:', transformedBeats);

      return transformedBeats;
    } catch (error: any) {
      console.error('Error fetching beats for salesrep:', error);
      throw new Error(`API Error: ${error.message || 'Failed to fetch beats'}`);
    }
  }

  /**
   * Fetch ALL beats for a specific salesrep (auto-paginate until all data is loaded)
   */
  async fetchAllBeatsForSalesrep(salesrepId: string): Promise<Beat[]> {
    try {
      let allBeats: Beat[] = [];
      let offset = 0;
      const size = 1000;
      let hasMore = true;

      while (hasMore) {
        const batch = await this.fetchBeatsForSalesrep(salesrepId, size);
        allBeats = [...allBeats, ...batch];

        // If we got less than limit, we've fetched all data
        hasMore = batch.length === size;
        offset += batch.length;

        console.log(`Fetched ${batch.length} beats (total: ${allBeats.length})`);
      }

      console.log(`✅ Finished loading all beats: ${allBeats.length} total`);
      return allBeats;
    } catch (error) {
      console.error('Error fetching all beats:', error);
      throw error;
    }
  }
}

// Export a singleton instance
export const beatMigrationService = new BeatMigrationService();
export default beatMigrationService;
